import { useEffect, useMemo, useRef, useState } from 'react';
import { collection, doc, getDoc, onSnapshot, query, serverTimestamp, setDoc, where } from 'firebase/firestore';
import { CheckCircle2, Clock3, CreditCard, ExternalLink, MonitorPlay, PackageCheck, RefreshCcw, ShieldCheck } from 'lucide-react';
import { auth, db } from '../../lib/firebase';
import { useAuth } from '../../context/AuthContext';
import { useToast } from '../../context/ToastContext';
import PageHeader from '../../components/PageHeader';
import Card from '../../components/Card';
import Badge from '../../components/Badge';
import DataTable from '../../components/DataTable';
import { currency, formatDate, screenLimitLabel } from '../../utils/format';

export default function SubscriptionPage() {
  const { profile, tenant, subscription, refresh } = useAuth();
  const tenantId = profile?.tenantId;
  const { notify } = useToast();
  const [plans, setPlans] = useState([]);
  const [payments, setPayments] = useState([]);
  const [gateway, setGateway] = useState(null);
  const [busy, setBusy] = useState(null);
  const [checkout, setCheckout] = useState(null);
  const checkoutUnsub = useRef(null);

  useEffect(() => {
    getDoc(doc(db, 'system_settings', 'payment_gateway'))
      .then(s => setGateway(s.exists() ? s.data() : { enabled: false }))
      .catch(() => setGateway({ enabled: false }));

    const unsubs = [
      onSnapshot(query(collection(db, 'plans'), where('status', '==', 'active')), s => setPlans(s.docs.map(d => ({ id: d.id, ...d.data() })))),
      tenantId ? onSnapshot(query(collection(db, 'payments'), where('tenantId', '==', tenantId)), s => setPayments(s.docs.map(d => ({ id: d.id, ...d.data() })))) : () => {},
    ];
    return () => {
      unsubs.forEach(u => u?.());
      checkoutUnsub.current?.();
    };
  }, [tenantId]);

  const sortedPlans = useMemo(() => [...plans].sort((a,b)=>Number(a.price||0)-Number(b.price||0)), [plans]);

  async function payForPlan(plan) {
    if (!tenantId || !auth.currentUser) return;
    if (gateway?.enabled !== true) {
      notify('Online payment is not enabled by Super Admin.', 'error');
      return;
    }
    setBusy(plan.id);
    let popup = null;
    try {
      popup = window.open('', '_blank');
      const requestRef = doc(collection(db, 'subscription_checkout_requests'));
      await setDoc(requestRef, {
        tenantId,
        planId: plan.id,
        requestedBy: auth.currentUser.uid,
        customerEmail: profile?.email || auth.currentUser.email || tenant?.email || '',
        customerPhone: profile?.phone || tenant?.phone || '',
        client: 'react_admin',
        status: 'creating',
        createdAt: serverTimestamp(),
      });
      setCheckout({ id: requestRef.id, status: 'creating', planName: plan.name, amount: plan.price });

      checkoutUnsub.current?.();
      checkoutUnsub.current = onSnapshot(requestRef, async snap => {
        if (!snap.exists()) return;
        const data = { id: snap.id, ...snap.data() };
        setCheckout(data);
        if (data.status === 'ready' && data.paymentUrl) {
          if (popup && !popup.closed) popup.location.href = data.paymentUrl;
          else window.open(data.paymentUrl, '_blank', 'noopener,noreferrer');
          notify('Razorpay checkout opened. Complete payment, then return to this page.', 'success');
          setBusy(null);
        }
        if (data.status === 'paid') {
          checkoutUnsub.current?.();
          checkoutUnsub.current = null;
          if (popup && !popup.closed) popup.close();
          notify('Payment verified. Subscription activated.', 'success');
          setBusy(null);
          await refresh();
        }
        if (data.status === 'error' || data.status === 'failed') {
          checkoutUnsub.current?.();
          checkoutUnsub.current = null;
          if (popup && !popup.closed) popup.close();
          setBusy(null);
          notify(data.errorMessage || 'Payment could not be completed.', 'error');
        }
      });
    } catch (error) {
      if (popup && !popup.closed) popup.close();
      setBusy(null);
      notify(error.message, 'error');
    }
  }

  async function refreshCheckout() {
    if (!checkout?.id) return;
    const snap = await getDoc(doc(db, 'subscription_checkout_requests', checkout.id));
    if (snap.exists()) {
      const data = { id: snap.id, ...snap.data() };
      setCheckout(data);
      if (data.status === 'paid') await refresh();
      if (data.status === 'ready' && data.paymentUrl) window.open(data.paymentUrl, '_blank', 'noopener,noreferrer');
    }
  }

  return <>
    <PageHeader eyebrow="Billing & entitlement" title="Subscription" description="Choose a package and pay securely. TV playback is unlocked only after the payment webhook verifies the transaction." actions={<button className="btn btn-soft" onClick={refresh}><RefreshCcw size={17}/> Refresh entitlement</button>}/>

    <Card className={`subscription-banner ${subscription?.isActive?'active':subscription?.status==='expired'?'expired':'inactive'}`}>
      <div className="subscription-icon"><PackageCheck/></div><div className="grow"><span className="eyebrow">Current entitlement</span><h2>{subscription?.planName||'No active package'}</h2><p>{subscription?.message||'Choose a package below.'}</p><div className="meta-pills"><span><MonitorPlay size={14}/> {subscription?.usedScreens||0}/{screenLimitLabel(subscription?.maxScreens||0)} screens</span>{subscription?.endDate&&<span><Clock3 size={14}/> expires {formatDate(subscription.endDate)}</span>}</div></div><Badge tone={subscription?.isActive?'success':subscription?.status==='expired'?'danger':'warning'}>{subscription?.status||'inactive'}</Badge>
    </Card>

    <Card className={`gateway-availability ${gateway?.enabled?'gateway-online':'gateway-offline'}`}>
      <div className="gateway-availability-icon"><ShieldCheck/></div><div className="grow"><strong>{gateway?.enabled ? `${gateway.merchantName || 'Weilink Signage'} secure checkout` : 'Online payment unavailable'}</strong><p>{gateway?.enabled ? `Payments are processed through ${gateway.provider === 'razorpay' ? 'Razorpay' : gateway.provider}. UPI, cards, wallets and netbanking are available according to your merchant account.` : 'Super Admin must configure and enable a payment gateway first.'}</p></div><Badge tone={gateway?.enabled?'success':'warning'}>{gateway?.enabled?(gateway.mode||'test'):'disabled'}</Badge>
    </Card>

    {checkout && !['paid','error','failed'].includes(checkout.status) && <Card className="checkout-progress-card"><div className="checkout-spinner"/><div className="grow"><strong>{checkout.status === 'ready' ? 'Checkout ready / payment pending' : 'Preparing secure checkout…'}</strong><p>{checkout.planName || 'Subscription'} · {currency(checkout.amount || 0)}. Complete the Razorpay payment in the opened window.</p></div><button className="btn btn-soft" onClick={refreshCheckout}><ExternalLink size={16}/> Check / reopen</button></Card>}

    <div className="section-title page-section"><div><span className="eyebrow">Available packages</span><h3>Choose screen capacity</h3></div></div>
    <div className="plans-grid-react">{sortedPlans.map(plan=>{const current=subscription?.planId===plan.id&&subscription?.isActive;return <Card className={`plan-card-react ${plan.popular?'popular':''} ${current?'current':''}`} key={plan.id}>{plan.popular&&<span className="popular-tag">Most popular</span>}<h3>{plan.name}</h3><div className="plan-price"><strong>{currency(plan.price)}</strong><span>/{plan.billingCycle||'month'}</span></div><div className="plan-feature"><CheckCircle2/> <span>{screenLimitLabel(plan.screens??plan.maxScreens)} screens</span></div><div className="plan-feature"><CheckCircle2/> <span>{Number((plan.storage ?? plan.maxStorageGB) || 0)} GB storage</span></div>{(plan.features||[]).slice(0,5).map((feature,i)=><div className="plan-feature" key={i}><CheckCircle2/> <span>{feature}</span></div>)}<button className={`btn btn-wide ${current?'btn-soft':'btn-primary'}`} disabled={current||busy===plan.id||gateway?.enabled!==true} onClick={()=>payForPlan(plan)}><CreditCard size={17}/>{current?'Current package':busy===plan.id?'Preparing payment…':gateway?.enabled===true?'Choose & Pay':'Gateway unavailable'}</button></Card>})}</div>

    <Card><div className="card-head"><div><span className="eyebrow">Payment ledger</span><h3>Payment history</h3></div><CreditCard size={19}/></div><DataTable rows={[...payments].sort((a,b)=>(b.paymentDate?.seconds||0)-(a.paymentDate?.seconds||0))} columns={[{key:'date',label:'Date',render:p=>formatDate(p.paymentDate||p.createdAt)},{key:'plan',label:'Plan',render:p=>p.planName||'—'},{key:'amount',label:'Amount',render:p=>currency(p.amount)},{key:'gateway',label:'Gateway',render:p=>p.gateway||'manual'},{key:'status',label:'Status',render:p=><Badge tone={['paid','verified','success','completed'].includes(String(p.status||'').toLowerCase()) ? 'success' : 'neutral'}>{p.status || 'recorded'}</Badge>}]} /></Card>
  </>;
}
